import React from 'react';
import { createPortal } from 'react-dom';
import { Typography, Button, theme } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { IconTool, IconLock } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { IAvailableMachineryAccessory } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { TEAL, SURFACE_1_SOFT, pillToneStyle } from '../theme/creamyTokens';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';

const { Text, Title } = Typography;

interface AccessoryPickerSheetProps {
  open: boolean;
  accessories: IAvailableMachineryAccessory[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  onClose: () => void;
  /** Days in the guest's own selected rental range, once picked -- see AvailabilityCalendar/HeavyDutyMachineryBookingWidget's own computeRentalDays call. Undefined before any dates are chosen: rows show each accessory's flat per-day rate, the footer total shows a "select your dates" pending state instead of a number, same as the widget's own insuranceCostEstimate pending state. */
  rentalDays?: number;
}

/**
 * Full-screen accessory picker for booking checkout -- the inline pill row
 * this replaces (a small wrap of chips sandwiched between insurance and the
 * calendar) was easy to scroll past on a long mobile page. Same portal +
 * framer-motion mechanics as ActionSheet/ConfirmBottomSheet, but covers the
 * whole viewport instead of a partial sheet, since a scrollable list of
 * full-width rows (one per accessory, name+description+price) needs the
 * room to stay legible. Teal, not coral -- this is the guest checkout
 * widget's own accent (see HeavyDutyMachineryBookingWidget's
 * bookingPillTheme), never the host-facing coral scope.
 *
 * An accessory with no free units for the guest's own dates stays in the
 * list rather than being dropped -- same reduced-opacity "shown disabled,
 * not hidden" treatment (+ "Reserved" pill + "Available from" text)
 * HeavyDutyMachineryBookingWidget's own operator rows already use, reused
 * as-is here rather than a new pattern.
 */
const AccessoryPickerSheet: React.FC<AccessoryPickerSheetProps> = ({ open, accessories, selectedIds, onToggle, onClose, rentalDays }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();

  const selectedPerDayTotal = accessories.filter((a) => selectedIds.includes(a.id)).reduce((sum, a) => sum + a.pricePerDay, 0);
  const total = rentalDays ? selectedPerDayTotal * rentalDays : undefined;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="accessory-picker-sheet"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.28, ease: 'easeInOut' }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2200,
            background: token.colorBgContainer,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '16px 12px 12px 20px',
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
              flexShrink: 0,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_SHEET_TITLE)}
            </Title>
            <Button type="text" shape="circle" icon={<CloseOutlined />} onClick={onClose} />
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {accessories.map((accessory) => {
              const isSelected = selectedIds.includes(accessory.id);
              const isUnavailable = !accessory.isAvailableForRequestedDates;
              const thumbnail = accessory.photoUrls[0];
              return (
                <motion.div key={accessory.id} whileTap={isUnavailable ? undefined : { scale: 0.98 }}>
                  <div
                    onClick={isUnavailable ? undefined : () => onToggle(accessory.id)}
                    style={{
                      padding: '14px 16px',
                      borderRadius: 16,
                      border: `${isSelected ? 2 : 1}px solid ${isSelected ? TEAL : token.colorBorderSecondary}`,
                      background: isSelected ? SURFACE_1_SOFT : token.colorBgContainer,
                      opacity: isUnavailable ? 0.6 : 1,
                      cursor: isUnavailable ? 'default' : 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      {thumbnail ? (
                        <img
                          src={thumbnail}
                          alt={accessory.name}
                          style={{ width: 40, height: 40, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                        />
                      ) : (
                        <span
                          style={{
                            display: 'flex',
                            flexShrink: 0,
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isSelected ? TEAL : token.colorFillTertiary,
                            color: isSelected ? '#fff' : token.colorTextSecondary,
                          }}
                        >
                          <IconTool size={18} />
                        </span>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong={isSelected} style={{ display: 'block' }}>
                          {accessory.name}
                        </Text>
                        {accessory.description && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {accessory.description}
                          </Text>
                        )}
                      </div>
                      {isUnavailable ? (
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '2px 10px',
                            borderRadius: 999,
                            fontSize: 11,
                            fontWeight: 600,
                            flexShrink: 0,
                            ...pillToneStyle('warning', token),
                          }}
                        >
                          <IconLock size={12} />
                          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORY_UNAVAILABLE_LABEL)}
                        </span>
                      ) : (
                        <Text strong style={{ color: isSelected ? TEAL : token.colorText, whiteSpace: 'nowrap' }}>
                          +{accessory.pricePerDay.toFixed(2)}€/{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX)}
                        </Text>
                      )}
                    </div>
                    {isUnavailable && accessory.availableFromDate && (
                      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORY_AVAILABLE_FROM_LABEL, {
                          date: formatFullDate(dayjs(accessory.availableFromDate), i18n.language),
                        })}
                      </Text>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div
            style={{
              flexShrink: 0,
              padding: '12px 16px calc(16px + env(safe-area-inset-bottom, 0px))',
              borderTop: `1px solid ${token.colorBorderSecondary}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
            }}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_LABEL)}
              </Text>
              {total != null ? (
                <>
                  <Text strong style={{ fontSize: 18, color: TEAL }}>
                    {total.toFixed(2)}€
                  </Text>
                  {/* An accessory bills per rental day like the machine does,
                      so the total jumps with the dates -- saying so here
                      stops it reading as an unexplained number. */}
                  <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>
                    {selectedPerDayTotal.toFixed(2)}€/{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX)} × {rentalDays}{' '}
                    {translate(t, rentalDays === 1 ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAYS_SUFFIX)}
                  </Text>
                </>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_COST_PENDING)}
                </Text>
              )}
            </div>
            <Button type="primary" shape="round" onClick={onClose} style={{ background: TEAL, borderColor: TEAL }}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_ACCESSORIES_DONE_BUTTON)}
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default AccessoryPickerSheet;
